import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyReportsByUserQuery } from '../../../../hooks/queries/useReportsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { ReportWithActivities } from '../../../../services/apiSLTP/dailyReports/reports';
import { Site } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { reportTypeEnum, systemConfigKeyEnum } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import GenericAgGridTable from '../../GenericAgGridTable';
import { activityTypeEnum, siteTypeEnum } from '../../../../constants';

interface PayrollReportsTableProps {
    currentDate: Date;
}

interface PayrollLineItem {
    id: string;
    payrollNumber: string;
    name: string;
    code: string;
    quantity: number;
    unit: string;
}

const PayrollReportsTable: React.FC<PayrollReportsTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const { data: dataMonthlyReports, isLoading: isMonthlyReportsLoading } = useMonthlyReportsByUserQuery({
        params: { date: currentDate.toISOString() },
    });

    const getFirstSite = (report: ReportWithActivities): Site | null => {
        if (!report.activities.length) return null;
        const sortedActivities = [...report.activities.filter((activity) => activity.siteId?.type !== siteTypeEnum.EXTERNAL)].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        return sortedActivities[0]?.siteId || null;
    };

    const rowData = useMemo(() => {
        if (!dataMonthlyReports) return [];

        const payrollLines: PayrollLineItem[] = [];

        dataMonthlyReports
            .filter((userWithReports) => !userWithReports.user.isSpecial)
            .forEach((userWithReports) => {
                const user = userWithReports.user;
                const userName = formatName(user.firstName, user.lastName);
                const payrollNumber = user.payrollNumber || '';
                const jobHasTravelZones = user.jobId?.hasTravelZones ?? false;
                const hasMealAllowance = user.jobId?.hasMealAllowance ?? false;
                const hasPhoneAllowance = user.jobId?.hasPhoneAllowance ?? false;

                const reportsWithActivities = userWithReports.reportsByDate.filter(
                    (reportByDate) => reportByDate.report.reportType === reportTypeEnum.SITE && reportByDate.report.activities.length > 0
                );

                if (jobHasTravelZones) {
                    const zoneCountMap = new Map<string, { code: string; name: string; count: number }>();

                    reportsWithActivities.forEach((reportByDate) => {
                        const report = reportByDate.report;

                        if (report.isLongTrip) return;
                        if (report.activities.some((activity) => activity.siteId?.type === siteTypeEnum.TRAINING)) return;

                        const firstSite = getFirstSite(report);
                        const zone = firstSite?.workTravelZoneId;

                        if (zone && zone._id && zone.payrollCode) {
                            const existing = zoneCountMap.get(zone._id);
                            if (existing) {
                                existing.count++;
                            } else {
                                zoneCountMap.set(zone._id, {
                                    code: zone.payrollCode,
                                    name: zone.name || '',
                                    count: 1,
                                });
                            }
                        }
                    });

                    zoneCountMap.forEach((zoneData, zoneId) => {
                        payrollLines.push({
                            id: `${user._id}-zone-${zoneId}`,
                            payrollNumber,
                            name: userName,
                            code: zoneData.code,
                            quantity: zoneData.count,
                            unit: t('days'),
                        });
                    });
                }

                const longTripCount = reportsWithActivities.filter((reportByDate) => reportByDate.report.isLongTrip).length;

                if (longTripCount > 0) {
                    const longTripCode = getConfigValue(systemConfigKeyEnum.PAYROLL_LONG_DISTANCE_CODE);
                    if (longTripCode) {
                        payrollLines.push({
                            id: `${user._id}-longtrip`,
                            payrollNumber,
                            name: userName,
                            code: longTripCode,
                            quantity: longTripCount,
                            unit: t('days'),
                        });
                    }
                }

                if (hasMealAllowance && reportsWithActivities.length > 0) {
                    const mealCode = getConfigValue(systemConfigKeyEnum.PAYROLL_MEAL_BASKET_CODE);
                    if (mealCode) {
                        payrollLines.push({
                            id: `${user._id}-meal`,
                            payrollNumber,
                            name: userName,
                            code: mealCode,
                            quantity: reportsWithActivities.length,
                            unit: t('days'),
                        });
                    }
                }

                if (hasPhoneAllowance && reportsWithActivities.length > 0) {
                    const phoneCode = getConfigValue(systemConfigKeyEnum.PAYROLL_PHONE_ALLOWANCE_CODE);
                    const phoneAmount = getConfigValue(systemConfigKeyEnum.PAYROLL_PHONE_ALLOWANCE_AMOUNT);

                    if (phoneCode && phoneAmount) {
                        const totalPhoneAllowance = phoneAmount * reportsWithActivities.length;
                        payrollLines.push({
                            id: `${user._id}-phone`,
                            payrollNumber,
                            name: userName,
                            code: phoneCode,
                            quantity: totalPhoneAllowance,
                            unit: getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL) || '€',
                        });
                    }
                }
            });

        return payrollLines.sort((a, b) => {
            const nameCompare = a.name.localeCompare(b.name);
            if (nameCompare !== 0) return nameCompare;
            return a.code.localeCompare(b.code);
        });
    }, [dataMonthlyReports, getConfigValue, t]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: capitalizeFirstLetter(t('registrationNumber')),
                field: 'payrollNumber',
                colId: 'payrollNumber',
                initialPinned: 'left',
            },
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'name',
                colId: 'name',
                initialPinned: 'left',
            },
            {
                headerName: capitalizeFirstLetter(t('code')),
                field: 'code',
                colId: 'code',
                initialFlex: 1
            },
            {
                headerName: capitalizeFirstLetter(t('value')),
                field: 'quantity',
                colId: 'quantity',
                type: 'numericColumn',
                valueFormatter: (params) => {
                    return formatNumber(params.value, t('locale'));
                },
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('unit')),
                field: 'unit',
                colId: 'unit',
            },
        ] as ColDef[],
        [t]
    );

    return (
        <GenericAgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            exportCsvFileName={`payroll-${dayjs(currentDate).format('YYYY-MM')}`}
            agGridProps={{ getRowId: ({ data }) => data.id }}
            loading={isMonthlyReportsLoading}
        />
    );
};

export default PayrollReportsTable;
